import { useQuery } from '@tanstack/vue-query'
import { computed, type MaybeRefOrGetter, ref, watch } from 'vue'
import { toValue } from 'vue'

import { getTaggableUsers } from '@/features/chat/api'

export function useMentions(conversationId: MaybeRefOrGetter<string | null>) {
  const showMentionDropdown = ref(false)
  const mentionSearchQuery = ref('')
  const mentionStartIndex = ref(-1)

  const conversationIdRef = computed(() => toValue(conversationId))

  const {
    data: taggableUsersRef,
    isLoading: isLoadingTaggableUsersRef,
    refetch: refetchTaggableUsers,
  } = useQuery({
    queryKey: computed(() => ['taggable-users', conversationIdRef.value, mentionSearchQuery.value]),
    queryFn: async () => {
      const convId = conversationIdRef.value
      if (!convId) return []
      try {
        const users = await getTaggableUsers({
          conversationId: convId,
          search: mentionSearchQuery.value || undefined,
        })
        return users || []
      } catch (error) {
        console.error('Error fetching taggable users:', error)
        return []
      }
    },
    enabled: computed(() => !!conversationIdRef.value && showMentionDropdown.value),
    staleTime: 0,
  })

  watch(showMentionDropdown, (isOpen) => {
    if (isOpen && conversationIdRef.value) {
      refetchTaggableUsers()
    }
  })

  function parseMentions(message: string): string[] {
    const mentionRegex = /@([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/gi
    const matches = message.match(mentionRegex)
    if (!matches) {
      return []
    }
    return matches.map((match) => match.substring(1))
  }

  function findMentionPosition(text: string, cursorPosition: number): { start: number; end: number } | null {
    let start = cursorPosition - 1
    while (start >= 0 && text[start] !== '@' && text[start] !== ' ' && text[start] !== '\n') {
      start--
    }

    if (start < 0 || text[start] !== '@') {
      return null
    }

    let end = start + 1
    while (end < text.length && text[end] !== ' ' && text[end] !== '\n') {
      end++
    }

    return { start, end }
  }

  function insertMention(
    text: string,
    cursorPosition: number,
    userId: string,
    userName: string,
  ): { newText: string; newCursorPosition: number; displayText: string } {
    const mentionDisplay = `@${userName}`
    const mentionInternal = `@[${userId}|${userName}]`

    const before = text.substring(0, cursorPosition)
    const after = text.substring(cursorPosition)

    const mentionPos = findMentionPosition(text, cursorPosition)
    if (mentionPos) {
      const beforeMention = text.substring(0, mentionPos.start)
      const afterMention = text.substring(mentionPos.end)
      const newText = beforeMention + mentionInternal + ' ' + afterMention
      const newCursorPosition = beforeMention.length + mentionInternal.length + 1
      return { newText, newCursorPosition, displayText: mentionDisplay }
    }

    const newText = before + mentionInternal + ' ' + after
    const newCursorPosition = before.length + mentionInternal.length + 1
    return { newText, newCursorPosition, displayText: mentionDisplay }
  }

  function formatMentionForDisplay(text: string): string {
    const mentionRegex = /@\[([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})\|([^\]]+)\]/g
    return text.replace(mentionRegex, '@$2')
  }

  function convertMentionsForSend(text: string): string {
    const mentionRegex = /@\[([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})\|[^\]]+\]/g
    return text.replace(mentionRegex, '@$1')
  }

  function checkForMention(text: string, cursorPosition: number): boolean {
    if (cursorPosition === 0) {
      showMentionDropdown.value = false
      return false
    }

    const charBefore = text[cursorPosition - 1]
    if (charBefore === '@') {
      showMentionDropdown.value = true
      mentionStartIndex.value = cursorPosition - 1
      mentionSearchQuery.value = ''
      return true
    }

    const mentionPos = findMentionPosition(text, cursorPosition)
    if (mentionPos && text[mentionPos.start] === '@') {
      const textAfterAt = text.substring(mentionPos.start + 1, cursorPosition)

      if (textAfterAt.startsWith('[') && textAfterAt.includes('|')) {
        showMentionDropdown.value = false
        return false
      }

      showMentionDropdown.value = true
      mentionStartIndex.value = mentionPos.start
      mentionSearchQuery.value = textAfterAt
      return true
    }

    showMentionDropdown.value = false
    return false
  }

  return {
    showMentionDropdown,
    mentionSearchQuery,
    mentionStartIndex,
    taggableUsersRef,
    isLoadingTaggableUsersRef,
    parseMentions,
    findMentionPosition,
    insertMention,
    checkForMention,
    formatMentionForDisplay,
    convertMentionsForSend,
  }
}
